"use client";

export const runtime = "edge";

import { useEffect } from "react";
import { useTranslations } from "next-intl";
import { Link } from "@/i18n/routing";
import { Marginalia } from "@/components/ui/Marginalia";
import { Button, buttonClasses } from "@/components/ui/Button";
import { captureAppCrash } from "@/lib/posthog";

/**
 * Catches any render or effect crash below the locale layout — every funnel step
 * and every marketing page. Without it Next serves its own bare screen
 * ("Application error: a client-side exception has occurred"), which drops the
 * header, the footer and the brand, offers no way back, and reads to a customer
 * as a dead shop. This keeps the chrome, says what happened, and makes retrying
 * one tap: `reset()` re-renders the segment, which is enough for the transient
 * failures (a chunk that did not download, an API blip) that most crashes are.
 */
export default function LocaleError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const t = useTranslations("error");

  useEffect(() => {
    captureAppCrash(error, "locale");
  }, [error]);

  return (
    <main className="flex min-h-[calc(100svh-65px)] flex-col">
      <section className="mx-auto flex max-w-page flex-1 flex-col justify-center px-6 py-16 md:py-24 lg:px-12">
        <div className="max-w-xl space-y-6">
          <Marginalia>{t("eyebrow")}</Marginalia>
          <h1 className="font-display text-display-lg tracking-[-0.015em] text-ink">{t("heading")}</h1>
          <p className="font-sans text-body-lg text-ink-soft">{t("body")}</p>
          <div className="flex flex-wrap items-center gap-6 pt-2">
            <Button onClick={reset} variant="primary">
              {t("ctaRetry")}
            </Button>
            <Link className={buttonClasses("ghost")} href="/">
              {t("ctaHome")}
            </Link>
          </div>
          <p className="pt-6 font-mono text-xs text-ink-faint">
            {t("supportPrefix")}{" "}
            <a className="underline underline-offset-4" href="mailto:contact@vellumprintworks.com">
              contact@vellumprintworks.com
            </a>
          </p>
          {/* The digest is the only handle that ties what the customer sees to the
              captured exception, so it has to be on screen for them to quote. */}
          {error.digest ? (
            <p className="font-mono text-xs text-ink-faint">{t("reference", { digest: error.digest })}</p>
          ) : null}
        </div>
      </section>
    </main>
  );
}
